import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { WhiteboardsMenu, CaptureUpdateAction } from "@excalidraw/excalidraw";
import { calculateScrollCenter } from "@excalidraw/excalidraw/scene";

import {
  useAtom,
  useAtomValue,
  useSetAtom,
  useAtom as useAtomValue2,
} from "../app-jotai";
import {
  currentProfileAtom,
  whiteboardsAtom,
  currentWhiteboardAtom,
  saveStateAtom,
  lastSavedStateAtom,
  cancelSaveAtom,
  whiteboardFoldersAtom,
  whiteboardSwitchingAtom,
} from "../app-jotai";
import {
  createWhiteboard,
  updateWhiteboardLastOpened,
  deleteWhiteboard,
  renameWhiteboard,
  loadWhiteboardFolders,
  createWhiteboardFolder,
  renameWhiteboardFolder,
  deleteWhiteboardFolder,
  moveWhiteboardToFolder,
  updateWhiteboardPinned,
} from "../data/firestore";
import { deleteWhiteboardThumbnail } from "../data/storage";
import { whiteboardCache } from "../data/whiteboardCache";

interface WhiteboardsMenuContainerProps {
  excalidrawAPI?: ExcalidrawImperativeAPI | null;
}

export const WhiteboardsMenuContainer = ({
  excalidrawAPI,
}: WhiteboardsMenuContainerProps) => {
  const currentProfile = useAtomValue(currentProfileAtom);
  const [whiteboards, setWhiteboards] = useAtom(whiteboardsAtom);
  const [currentWhiteboard, setCurrentWhiteboard] = useAtom(
    currentWhiteboardAtom,
  );
  const saveState = useAtomValue(saveStateAtom);
  const setLastSavedState = useSetAtom(lastSavedStateAtom);
  const setSaveState = useSetAtom(saveStateAtom);
  const cancelSave = useAtomValue(cancelSaveAtom);
  const [folders, setFolders] = useAtom(whiteboardFoldersAtom);
  const setWhiteboardSwitching = useSetAtom(whiteboardSwitchingAtom);

  const handleSelectWhiteboard = async (whiteboardId: string) => {
    const whiteboard = whiteboards.find((wb) => wb.id === whiteboardId);
    if (!whiteboard || !excalidrawAPI) {
      return;
    }

    // Check for unsaved changes
    if (
      currentWhiteboard?.id !== whiteboardId &&
      saveState === "unsaved" &&
      !confirm("You have unsaved changes. Switch whiteboard anyway?")
    ) {
      return;
    }

    try {
      // Set loading state
      setWhiteboardSwitching(true);

      // Load whiteboard content on-demand (with caching)
      const content = await whiteboardCache.get(whiteboardId);
      const loadedWhiteboard = {
        ...whiteboard,
        elements: content.elements,
        files: content.files,
      };

      // Switch to new whiteboard
      setCurrentWhiteboard(loadedWhiteboard);

      // Update the whiteboards array cache with loaded content
      setWhiteboards(
        whiteboards.map((wb) =>
          wb.id === whiteboardId ? loadedWhiteboard : wb,
        ),
      );

      await updateWhiteboardLastOpened(whiteboardId);

      // Cancel any pending saves from the previous whiteboard
      if (cancelSave) {
        cancelSave();
      }

      // Clear lastSavedState BEFORE updateScene to prevent race condition
      // This ensures onChange will initialize it from loaded elements
      setLastSavedState(null);

      // Load whiteboard content into Excalidraw (don't capture in history)
      excalidrawAPI.updateScene({
        elements: content.elements || [],
        captureUpdate: CaptureUpdateAction.NEVER,
      });

      if (content.files && Object.keys(content.files).length > 0) {
        excalidrawAPI.addFiles(
          Object.entries(content.files).map(([id, file]) => ({
            ...file,
            id: id as any,
          })),
        );
      }

      // Clear undo/redo history for fresh start
      excalidrawAPI.history.clear();

      // Apply scroll center after elements are loaded
      const scrollCenter = calculateScrollCenter(
        content.elements || [],
        excalidrawAPI.getAppState(),
      );
      excalidrawAPI.updateScene({
        appState: scrollCenter,
      });
    } finally {
      // Clear loading state
      setWhiteboardSwitching(false);
    }
  };

  const handleCreateWhiteboard = async (name: string) => {
    if (!currentProfile) {
      return;
    }

    // Check for unsaved changes
    if (
      saveState === "unsaved" &&
      !confirm("You have unsaved changes. Create new whiteboard anyway?")
    ) {
      return;
    }

    try {
      // Create new whiteboard
      const newWhiteboard = await createWhiteboard(currentProfile.id, name);
      setWhiteboards([...whiteboards, newWhiteboard]);
      setCurrentWhiteboard(newWhiteboard);

      // Clear canvas for new whiteboard
      if (excalidrawAPI) {
        excalidrawAPI.updateScene({
          elements: [],
        });
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to create whiteboard");
    }
  };

  const handleDeleteWhiteboard = async (whiteboardId: string) => {
    try {
      await deleteWhiteboard(whiteboardId);
      // Delete thumbnail from storage
      await deleteWhiteboardThumbnail(whiteboardId);

      const updatedWhiteboards = whiteboards.filter(
        (wb) => wb.id !== whiteboardId,
      );
      setWhiteboards(updatedWhiteboards);

      // If deleting current whiteboard, switch to another or clear
      if (currentWhiteboard?.id === whiteboardId) {
        if (updatedWhiteboards.length > 0) {
          const newCurrent = updatedWhiteboards[0];
          setCurrentWhiteboard(newCurrent);
          if (excalidrawAPI) {
            excalidrawAPI.updateScene({
              elements: newCurrent.elements || [],
            });
            if (newCurrent.files && Object.keys(newCurrent.files).length > 0) {
              excalidrawAPI.addFiles(
                Object.entries(newCurrent.files).map(([id, file]) => ({
                  ...file,
                  id: id as any,
                })),
              );
            }
          }
        } else {
          setCurrentWhiteboard(null);
          if (excalidrawAPI) {
            excalidrawAPI.updateScene({ elements: [] });
          }
        }
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  const handleRenameWhiteboard = async (whiteboardId: string, name: string) => {
    try {
      await renameWhiteboard(whiteboardId, name);
      // Update local state
      setWhiteboards(
        whiteboards.map((wb) =>
          wb.id === whiteboardId ? { ...wb, name } : wb,
        ),
      );
      if (currentWhiteboard?.id === whiteboardId) {
        setCurrentWhiteboard({ ...currentWhiteboard, name });
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to rename");
    }
  };

  const handleCreateFolder = async (name: string, parentId: string | null) => {
    if (!currentProfile) {
      return;
    }
    try {
      const newFolder = await createWhiteboardFolder(
        currentProfile.id,
        name,
        parentId,
      );
      setFolders([...folders, newFolder]);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to create folder");
    }
  };

  const handleRenameFolder = async (folderId: string, name: string) => {
    try {
      await renameWhiteboardFolder(folderId, name);
      setFolders(folders.map((f) => (f.id === folderId ? { ...f, name } : f)));
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to rename folder");
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      await deleteWhiteboardFolder(folderId);
      // Remove folder and all its children
      const removeFolderAndChildren = (id: string): string[] => {
        const childIds = folders
          .filter((f) => f.parentId === id)
          .flatMap((f) => removeFolderAndChildren(f.id));
        return [id, ...childIds];
      };
      const toRemove = new Set(removeFolderAndChildren(folderId));
      setFolders(folders.filter((f) => !toRemove.has(f.id)));
      // Move whiteboards out of deleted folders
      setWhiteboards(
        whiteboards.map((wb) =>
          wb.folderId && toRemove.has(wb.folderId)
            ? { ...wb, folderId: null }
            : wb,
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete folder");
    }
  };

  const handleMoveWhiteboard = async (
    whiteboardId: string,
    folderId: string | null,
  ) => {
    try {
      await moveWhiteboardToFolder(whiteboardId, folderId);
      setWhiteboards(
        whiteboards.map((wb) =>
          wb.id === whiteboardId ? { ...wb, folderId } : wb,
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to move whiteboard");
    }
  };

  const handlePinWhiteboard = async (whiteboardId: string, pinned: boolean) => {
    try {
      await updateWhiteboardPinned(whiteboardId, pinned);
      setWhiteboards(
        whiteboards.map((wb) =>
          wb.id === whiteboardId ? { ...wb, pinned } : wb,
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to pin whiteboard");
    }
  };

  if (!currentProfile) {
    return (
      <div style={{ padding: "1rem", textAlign: "center" }}>
        No profile selected
      </div>
    );
  }

  return (
    <WhiteboardsMenu
      whiteboards={whiteboards}
      folders={folders}
      currentWhiteboard={currentWhiteboard}
      onSelectWhiteboard={handleSelectWhiteboard}
      onCreateWhiteboard={handleCreateWhiteboard}
      onDeleteWhiteboard={handleDeleteWhiteboard}
      onRenameWhiteboard={handleRenameWhiteboard}
      onCreateFolder={handleCreateFolder}
      onRenameFolder={handleRenameFolder}
      onDeleteFolder={handleDeleteFolder}
      onMoveWhiteboard={handleMoveWhiteboard}
      onPinWhiteboard={handlePinWhiteboard}
    />
  );
};
